import { useCallback, useEffect, useRef, useState } from "react";
import { getHealthSummary } from "../../../services/content-health/content-health.service";
import { getProgramTree } from "../../../services/content-tree/content-tree.service";
import { AttentionItem, collectAttentionItems, sortBySeverity } from "./utils";

// Data source for the attention queue: the health summary says WHICH
// programs have issues (per-program counts only), and the per-node detail
// comes from each program's content tree — fetched only for the programs
// that actually report issues, then flattened and sorted once per load
// (memoized in state, not per render).

export interface AttentionDataState {
  items: AttentionItem[];
  loading: boolean;
  error: Error | null;
  /** ISO timestamp of the backend health summary (null until loaded). */
  generatedAt: string | null;
  /**
   * Titles of programs whose issues may be missing from `items`: the backend
   * couldn't analyze them, their tree request failed, or their tree came back
   * truncated. Non-empty ⇒ the list is partial, never "all healthy".
   */
  incompletePrograms: string[];
}

const INITIAL_STATE: AttentionDataState = {
  items: [],
  loading: true,
  error: null,
  generatedAt: null,
  incompletePrograms: [],
};

export const useAttentionData = () => {
  const [state, setState] = useState<AttentionDataState>(INITIAL_STATE);

  // "Last request wins": a slow, stale load can't clobber a newer retry
  // (same pattern as ContentMap and the palette preview).
  const latestRequestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const summary = await getHealthSummary();
      // Failed programs have zeroed counts and no analyzable tree: they are
      // surfaced as "incomplete", not silently dropped.
      const failedTitles = summary.programs
        .filter((program) => program.failed)
        .map((program) => program.title);
      const programsWithIssues = summary.programs.filter(
        (program) => program.issues > 0 && !program.failed
      );

      // allSettled: one failing tree must not hide the other programs' items.
      const results = await Promise.allSettled(
        programsWithIssues.map((program) => getProgramTree(program.id))
      );
      if (requestId !== latestRequestIdRef.current) return;

      const items: AttentionItem[] = [];
      const incompletePrograms = [...failedTitles];
      programsWithIssues.forEach((program, index) => {
        const result = results[index];
        if (result.status === "fulfilled") {
          items.push(...collectAttentionItems(result.value.root, program));
          // A capped tree may be missing unhealthy branches.
          if (result.value.truncated) incompletePrograms.push(program.title);
        } else {
          incompletePrograms.push(program.title);
        }
      });

      setState({
        items: sortBySeverity(items),
        loading: false,
        error: null,
        generatedAt: summary.generated_at,
        incompletePrograms,
      });
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) return;
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err as Error,
      }));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { ...state, refetch: fetchData };
};
